import React, { useState, useEffect, useCallback } from "react";
import {
  PageHeader, KPIGrid, DataTable, StandardModal, StatusBadge,
  FilterPills, Alert, Btn, FormField,
  inputStyle, textareaStyle, selectStyle,
  colors, spacing, typography, radius,
} from "../design-system";

const BASE = import.meta.env.VITE_API_URL || "http://localhost:8000/api";
const API = `${BASE}/surveillance`;

const TYPE_COLORS = {
  surveillance: colors.info,
  renewal:      colors.purple,
  witness:      colors.warning,
};

function TypeBadge({ type }) {
  const color = TYPE_COLORS[type] || colors.textMuted;
  return (
    <span style={{
      background: color + "22", color,
      padding: "2px 10px", borderRadius: radius.pill,
      fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.semibold,
      textTransform: "capitalize",
    }}>
      {type}
    </span>
  );
}

function NcEditor({ nonconformities, onChange }) {
  const add = () => onChange([...nonconformities, { description: "", severity: "minor", resolved: false }]);
  const update = (i, field, val) => {
    onChange(nonconformities.map((nc, idx) => idx === i ? { ...nc, [field]: val } : nc));
  };
  const remove = (i) => onChange(nonconformities.filter((_, idx) => idx !== i));

  return (
    <div>
      {nonconformities.map((nc, i) => (
        <div key={i} style={{ border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: spacing.md, marginBottom: spacing.sm }}>
          <div style={{ display: "flex", gap: spacing.sm, marginBottom: spacing.sm, alignItems: "center" }}>
            <select value={nc.severity} onChange={e => update(i, "severity", e.target.value)} style={{ ...selectStyle, width: "auto" }}>
              <option value="minor">Minor</option>
              <option value="major">Major</option>
            </select>
            <label style={{ display: "flex", alignItems: "center", gap: spacing.xs, fontSize: typography.fontSizes.md }}>
              <input type="checkbox" checked={nc.resolved} onChange={e => update(i, "resolved", e.target.checked)} />
              Resolved
            </label>
            <Btn size="sm" variant="danger" onClick={() => remove(i)} style={{ marginLeft: "auto" }}>Remove</Btn>
          </div>
          <textarea
            value={nc.description}
            onChange={e => update(i, "description", e.target.value)}
            placeholder="Nonconformity description..."
            rows={2}
            style={{ ...textareaStyle, minHeight: "60px" }}
          />
        </div>
      ))}
      <Btn size="sm" variant="secondary" onClick={add}>+ Add NC</Btn>
    </div>
  );
}

export default function SurveillanceDashboard() {
  const [audits, setAudits]       = useState([]);
  const [certBodies, setCertBodies] = useState([]);
  const [auditors, setAuditors]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType]     = useState("all");
  const [filterCB, setFilterCB]         = useState("");
  const [showCreate, setShowCreate]     = useState(false);
  const [showNC, setShowNC]             = useState(null);
  const [showConfirm, setShowConfirm]   = useState(null);
  const [createForm, setCreateForm]     = useState({
    cert_body_id: "", audit_type: "surveillance", scheduled_date: "", auditor_id: "", notes: "",
  });
  const [ncList, setNcList]   = useState([]);
  const [saving, setSaving]   = useState(false);
  const [msg, setMsg]         = useState("");
  const [msgType, setMsgType] = useState("success");

  const token    = localStorage.getItem("token");
  const userRole = JSON.parse(localStorage.getItem("user") || "{}").role;
  const isSuperAdmin = userRole === "super_admin";
  const headers  = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const fetchAudits = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus !== "all") params.set("status", filterStatus);
      if (filterType !== "all") params.set("audit_type", filterType);
      if (filterCB) params.set("cert_body_id", filterCB);
      const res = await fetch(`${API}/?${params}`, { headers });
      if (!res.ok) throw new Error("Failed to load surveillance audits");
      setAudits(await res.json());
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterType, filterCB]);

  useEffect(() => { fetchAudits(); }, [fetchAudits]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    fetch(`${BASE}/cert-bodies/`, { headers }).then(r => r.json()).then(d => setCertBodies(d.certification_bodies || d || [])).catch(() => {});
    fetch(`${BASE}/auditors/`, { headers }).then(r => r.json()).then(setAuditors).catch(() => {});
  }, []);

  const flash = (m, t = "success") => { setMsg(m); setMsgType(t); setTimeout(() => setMsg(""), 3500); };

  async function handleCreate(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${API}/`, {
        method: "POST", headers,
        body: JSON.stringify({
          cert_body_id:  parseInt(createForm.cert_body_id),
          audit_type:    createForm.audit_type,
          scheduled_date: createForm.scheduled_date,
          auditor_id:    createForm.auditor_id ? parseInt(createForm.auditor_id) : null,
          notes:         createForm.notes || null,
        }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || "Failed"); }
      flash("Surveillance audit scheduled.");
      setShowCreate(false);
      setCreateForm({ cert_body_id: "", audit_type: "surveillance", scheduled_date: "", auditor_id: "", notes: "" });
      fetchAudits();
    } catch (e) { flash("Error: " + e.message, "error"); }
    finally { setSaving(false); }
  }

  async function handleAction(auditId, action) {
    setSaving(true);
    try {
      const res = await fetch(`${API}/${auditId}/${action}`, { method: "PATCH", headers, body: JSON.stringify({}) });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || "Failed"); }
      flash(`Audit ${action}d successfully.`);
      setShowConfirm(null);
      fetchAudits();
    } catch (e) { flash("Error: " + e.message, "error"); }
    finally { setSaving(false); }
  }

  async function handleDelete(auditId) {
    if (!window.confirm("Permanently delete this surveillance audit? This cannot be undone.")) return;
    try {
      const res = await fetch(`${API}/${auditId}/`, { method: "DELETE", headers });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || "Failed"); }
      flash("Audit deleted.");
      fetchAudits();
    } catch (e) { flash("Error: " + e.message, "error"); }
  }

  async function handleSaveNC(auditId) {
    setSaving(true);
    try {
      const res = await fetch(`${API}/${auditId}/nonconformities`, {
        method: "PATCH", headers, body: JSON.stringify(ncList),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || "Failed"); }
      flash("Nonconformities saved.");
      setShowNC(null);
      fetchAudits();
    } catch (e) { flash("Error: " + e.message, "error"); }
    finally { setSaving(false); }
  }

  const overdueCount  = audits.filter(a => a.status === "overdue").length;
  const majorNcCount  = audits.filter(a => a.has_unresolved_major_nc).length;

  const kpis = [
    { label: "Total Audits",  value: audits.length,                         icon: "🔍", color: colors.primary },
    { label: "Overdue",       value: overdueCount,                           icon: "⚠️", color: overdueCount  > 0 ? colors.danger  : colors.success },
    { label: "Major NCs Open", value: majorNcCount,                          icon: "🔴", color: majorNcCount  > 0 ? colors.warning : colors.success },
    { label: "Completed",     value: audits.filter(a => a.status === "completed").length, icon: "✅", color: colors.success },
  ];

  const statusOptions = [
    { value: "all",         label: "All Status" },
    { value: "scheduled",   label: "Scheduled" },
    { value: "in_progress", label: "In Progress" },
    { value: "overdue",     label: "Overdue" },
    { value: "completed",   label: "Completed" },
    { value: "cancelled",   label: "Cancelled" },
  ];

  const typeOptions = [
    { value: "all",         label: "All Types" },
    { value: "surveillance", label: "Surveillance" },
    { value: "renewal",     label: "Renewal" },
    { value: "witness",     label: "Witness" },
  ];

  const columns = [
    { key: "id",           label: "ID",        render: v => <span style={{ color: colors.textMuted, fontFamily: "monospace" }}>#{v}</span> },
    { key: "cert_body_name", label: "Cert Body",
      render: (v, row) => <span style={{ fontWeight: typography.fontWeights.semibold }}>{v || `CB #${row.cert_body_id}`}</span> },
    { key: "audit_type",   label: "Type",      render: v => <TypeBadge type={v} /> },
    { key: "auditor_name", label: "Auditor",   render: v => v || "—" },
    { key: "scheduled_date", label: "Scheduled",
      render: v => v ? new Date(v).toLocaleDateString() : "—" },
    { key: "status",       label: "Status",    render: v => <StatusBadge status={v} /> },
    {
      key: "nonconformities",
      label: "NCs",
      render: (ncs) => {
        const list = ncs || [];
        const majorOpen = list.filter(nc => nc.severity === "major" && !nc.resolved).length;
        const minorOpen = list.filter(nc => nc.severity === "minor" && !nc.resolved).length;
        if (list.length === 0) return <span style={{ color: colors.textMuted }}>None</span>;
        if (majorOpen === 0 && minorOpen === 0) return <span style={{ color: colors.success }}>All resolved</span>;
        return (
          <span>
            {majorOpen > 0 && <span style={{ color: colors.danger, fontWeight: 600 }}>{majorOpen} major</span>}
            {majorOpen > 0 && minorOpen > 0 && ", "}
            {minorOpen > 0 && <span style={{ color: colors.warning }}>{minorOpen} minor</span>}
          </span>
        );
      },
    },
    {
      key: "_actions",
      label: "",
      align: "right",
      render: (_, audit) => {
        if (!isSuperAdmin) return null;
        return (
          <div style={{ display: "flex", gap: spacing.xs, justifyContent: "flex-end" }}>
            {audit.status === "scheduled" && (
              <Btn size="sm" variant="warning" onClick={() => setShowConfirm({ id: audit.id, action: "start", label: "Start" })}>Start</Btn>
            )}
            {audit.status === "in_progress" && (
              <Btn size="sm" variant="success" onClick={() => setShowConfirm({ id: audit.id, action: "complete", label: "Complete" })}>Complete</Btn>
            )}
            {["scheduled", "in_progress", "overdue"].includes(audit.status) && (
              <Btn size="sm" variant="danger" onClick={() => setShowConfirm({ id: audit.id, action: "cancel", label: "Cancel" })}>Cancel</Btn>
            )}
            {audit.status !== "cancelled" && (
              <Btn size="sm" variant="ghost" onClick={() => { setShowNC(audit); setNcList(audit.nonconformities || []); }}>NCs</Btn>
            )}
            <Btn size="sm" variant="danger" onClick={() => handleDelete(audit.id)}>Delete</Btn>
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <PageHeader
        title="Surveillance Audits"
        subtitle="Schedule and track surveillance audits for accredited certification bodies."
        actions={isSuperAdmin && <Btn onClick={() => setShowCreate(true)}>+ Schedule Audit</Btn>}
      />

      <KPIGrid kpis={kpis} columns={4} />

      {msg && <Alert type={msgType} message={msg} onClose={() => setMsg("")} />}

      {/* Filters */}
      <div style={{ display: "flex", gap: spacing.md, marginBottom: spacing.lg, flexWrap: "wrap", alignItems: "center" }}>
        <FilterPills options={statusOptions} value={filterStatus} onChange={setFilterStatus} />
        <FilterPills options={typeOptions}   value={filterType}   onChange={setFilterType} color={colors.purple} />
        {isSuperAdmin && certBodies.length > 0 && (
          <select value={filterCB} onChange={e => setFilterCB(e.target.value)} style={{ ...selectStyle, width: "auto" }}>
            <option value="">All CBs</option>
            {certBodies.map(cb => <option key={cb.id} value={cb.id}>{cb.name}</option>)}
          </select>
        )}
      </div>

      <DataTable columns={columns} rows={audits} loading={loading} emptyMessage="No surveillance audits found." keyField="id" />

      {/* Schedule Modal */}
      {showCreate && (
        <StandardModal
          title="Schedule Surveillance Audit"
          onClose={() => setShowCreate(false)}
          footer={
            <>
              <Btn variant="secondary" onClick={() => setShowCreate(false)}>Cancel</Btn>
              <Btn type="submit" form="create-audit-form" loading={saving}>Schedule Audit</Btn>
            </>
          }
        >
          <form id="create-audit-form" onSubmit={handleCreate}>
            <div style={{ display: "flex", flexDirection: "column", gap: spacing.md }}>
              <FormField label="Certification Body" required>
                <select required value={createForm.cert_body_id} onChange={e => setCreateForm(f => ({ ...f, cert_body_id: e.target.value }))} style={selectStyle}>
                  <option value="">Select CB...</option>
                  {certBodies.map(cb => <option key={cb.id} value={cb.id}>{cb.name}</option>)}
                </select>
              </FormField>
              <FormField label="Audit Type" required>
                <select value={createForm.audit_type} onChange={e => setCreateForm(f => ({ ...f, audit_type: e.target.value }))} style={selectStyle}>
                  <option value="surveillance">Surveillance</option>
                  <option value="renewal">Renewal</option>
                  <option value="witness">Witness</option>
                </select>
              </FormField>
              <FormField label="Scheduled Date" required>
                <input required type="datetime-local" value={createForm.scheduled_date} onChange={e => setCreateForm(f => ({ ...f, scheduled_date: e.target.value }))} style={inputStyle} />
              </FormField>
              <FormField label="Assign Auditor">
                <select value={createForm.auditor_id} onChange={e => setCreateForm(f => ({ ...f, auditor_id: e.target.value }))} style={selectStyle}>
                  <option value="">No auditor assigned</option>
                  {auditors.filter(a => a.status === "active").map(a => <option key={a.id} value={a.id}>{a.first_name} {a.last_name}</option>)}
                </select>
              </FormField>
              <FormField label="Notes">
                <textarea value={createForm.notes} onChange={e => setCreateForm(f => ({ ...f, notes: e.target.value }))} rows={3} style={textareaStyle} />
              </FormField>
            </div>
          </form>
        </StandardModal>
      )}

      {/* NC Editor Modal */}
      {showNC && (
        <StandardModal
          title={`Nonconformities — Audit #${showNC.id}`}
          onClose={() => setShowNC(null)}
          footer={
            <>
              <Btn variant="secondary" onClick={() => setShowNC(null)}>Cancel</Btn>
              <Btn onClick={() => handleSaveNC(showNC.id)} loading={saving}>Save NCs</Btn>
            </>
          }
        >
          <NcEditor nonconformities={ncList} onChange={setNcList} />
        </StandardModal>
      )}

      {/* Confirm Modal */}
      {showConfirm && (
        <StandardModal
          title={`${showConfirm.label} Audit #${showConfirm.id}`}
          onClose={() => setShowConfirm(null)}
          danger={showConfirm.action === "cancel"}
          footer={
            <>
              <Btn variant="secondary" onClick={() => setShowConfirm(null)}>Cancel</Btn>
              <Btn
                variant={showConfirm.action === "cancel" ? "danger" : "primary"}
                onClick={() => handleAction(showConfirm.id, showConfirm.action)}
                loading={saving}
              >
                {showConfirm.label}
              </Btn>
            </>
          }
        >
          <p style={{ color: colors.textSecondary, margin: 0 }}>
            Are you sure you want to <strong>{showConfirm.label.toLowerCase()}</strong> this surveillance audit?
            {showConfirm.action === "complete" && " This will automatically schedule the next surveillance in 12 months."}
          </p>
        </StandardModal>
      )}
    </div>
  );
}
